"use client";

import type { JSX } from "react";
import { useState } from "react";
import { Button, Input, Text, View } from "@/components";
import { useLayout, useNavigator } from "@/providers";

/**
 * @name VerifySearchForm
 * @description Ingreso manual del código de verificación (P2). Solo pide el
 * código: el servidor resuelve el tenant desde la propia inscripción. Solo
 * navega a la página de resultado: la verificación la hace el servidor.
 *
 * @returns {JSX.Element} El formulario renderizado.
 */
const VerifySearchForm = (): JSX.Element => {
  const { t } = useLayout();
  const { replace } = useNavigator();
  const [code, setCode] = useState("");

  const disabled = !code.trim();

  const handleVerify = () => {
    if (disabled) return;
    replace(`/elearning/verificar/${encodeURIComponent(code.trim())}`);
  };

  return (
    <View
      type="main"
      className="min-h-screen flex items-center justify-center bg-background p-6"
    >
      <View className="box w-full max-w-md p-8 rounded-3xl border border-border shadow-xl flex flex-col gap-6">
        <View className="flex flex-col items-center gap-1 text-center">
          <Text type="h1" className="text-lg font-semibold text-foreground">
            {t("certificateVerification")}
          </Text>
          <Text className="text-xs text-muted-foreground">
            {t("verifyFormHint")}
          </Text>
        </View>

        <View className="flex flex-col gap-4">
          <View className="flex flex-col gap-1.5">
            <Text
              type="label"
              htmlFor="verify-code"
              className="text-sm font-medium text-foreground"
            >
              {t("certificateCode")}
            </Text>
            <Input
              id="verify-code"
              name="verify-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SV-XXXXXXXX"
              className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm text-foreground"
            />
          </View>
          <Button
            action={handleVerify}
            disabled={disabled}
            label={t("verify")}
            className="w-full px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {t("verify")}
          </Button>
        </View>
      </View>
    </View>
  );
};

export default VerifySearchForm;
